// =============================================
// Parent Layout Component (Mobile-First)
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Baby,
  CalendarDays,
  MessageSquare,
  Settings,
  Bell,
  ChevronRight,
  LogOut,
  HelpCircle,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useHelpGuide } from '../../hooks/useHelpGuide';
import HelpGuideModal from '../../components/common/HelpGuideModal';

const navItems = [
  { path: '/parent/dashboard', icon: Home, label: 'בית' },
  { path: '/parent/children', icon: Baby, label: 'ילדים' },
  { path: '/parent/calendar', icon: CalendarDays, label: 'לוח שנה' },
  { path: '/parent/messages', icon: MessageSquare, label: 'הודעות' },
  { path: '/parent/settings', icon: Settings, label: 'הגדרות' },
];

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #fdfcfb 0%, #f9f6f3 100%)',
  },
  header: {
    backgroundColor: 'white',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    position: 'sticky' as const,
    top: 0,
    zIndex: 40,
    padding: '8px 16px',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  greeting: {
    fontSize: '11px',
    color: '#6b7280',
    marginBottom: '0px',
  },
  userName: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#1f2937',
  },
  notificationButton: {
    position: 'relative' as const,
    padding: '8px',
    borderRadius: '10px',
    backgroundColor: '#f9f6f3',
    border: 'none',
    cursor: 'pointer',
  },
  backButton: {
    padding: '8px',
    borderRadius: '10px',
    backgroundColor: '#f9f6f3',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  notificationDot: {
    position: 'absolute' as const,
    top: '8px',
    right: '8px',
    width: '10px',
    height: '10px',
    backgroundColor: '#ef4444',
    borderRadius: '50%',
    border: '2px solid white',
  },
  main: {
    padding: '12px 16px',
    paddingBottom: '90px',
  },
  nav: {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    borderTop: '1px solid #e8dfd5',
    padding: '8px 0',
    paddingBottom: 'env(safe-area-inset-bottom, 8px)',
    zIndex: 50,
  },
  navContent: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  navItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '4px',
    padding: '8px 16px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative' as const,
  },
  navLabel: {
    fontSize: '11px',
    fontWeight: '500',
  },
  activeIndicator: {
    position: 'absolute' as const,
    top: '-4px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '32px',
    height: '4px',
    backgroundColor: '#f97316',
    borderRadius: '4px',
  },
};

export default function ParentLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, token, logout } = useAuthStore();
  const helpGuide = useHelpGuide();

  // Hide layout on login page
  if (location.pathname === '/parent/login') {
    return <Outlet />;
  }
  
  return (
    <div style={styles.container} dir="rtl">
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          {/* Back Button or Greeting */}
          {location.pathname === '/parent/dashboard' ? (
            <div>
              <p style={styles.greeting}>שלום,</p>
              <h1 style={styles.userName}>
                {user?.name || user?.firstName || 'הורה יקר'}
              </h1>
            </div>
          ) : (
            <motion.button
              style={styles.backButton}
              onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/parent/dashboard')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronRight size={22} color="#4b5563" />
            </motion.button>
          )}

          {/* Logo + Help */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={helpGuide.open}
              style={{ padding: '6px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '8px' }}
              title="מדריך עזרה (Ctrl+/)"
            >
              <HelpCircle size={20} color="#9ca3af" />
            </button>
            <img
              src="/images/kipikid-logo.png"
              alt="Kipikid"
              style={{ width: '44px', height: '44px', objectFit: 'contain' }}
            />
          </div>

          {/* Logout (on dashboard) / Notifications (other pages) */}
          {location.pathname === '/parent/dashboard' ? (
            <motion.button
              style={styles.notificationButton}
              onClick={() => { logout(); navigate('/parent/login'); }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LogOut size={20} color="#ef4444" />
            </motion.button>
          ) : (
            <motion.button
              style={styles.notificationButton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Bell size={22} color="#4b5563" />
              <span style={styles.notificationDot} />
            </motion.button>
          )}
        </div>
      </header>
      
      {/* Main Content */}
      <main style={styles.main}>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
      
      <HelpGuideModal isOpen={helpGuide.isOpen} onClose={helpGuide.close} currentPath={helpGuide.currentPath} userRole={helpGuide.userRole} />

      {/* Bottom Navigation */}
      {(isAuthenticated || token) && (
        <nav style={styles.nav}>
          <div style={styles.navContent}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <motion.button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  style={{
                    ...styles.navItem,
                    color: isActive ? '#f97316' : '#9ca3af',
                  }}
                  whileTap={{ scale: 0.9 }}
                >
                  {isActive && <div style={styles.activeIndicator} />}
                  <motion.div animate={isActive ? { y: -2 } : { y: 0 }}>
                    <item.icon size={24} strokeWidth={isActive ? 2.5 : 2} />
                  </motion.div>
                  <span style={styles.navLabel}>{item.label}</span>
                </motion.button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
